import axios, { ResponseType } from "axios"
import createAuthRefreshInterceptor from "axios-auth-refresh"
import qs from "qs"
import config from "config/config"
import { IApiLink, IApiFollowLink, IMultipleEntities, IMultipleEntitiesResponse } from "@/types"
import { ApiLinkEntity, ApiLinkRel, ApiServiceName, ErrorCode } from "config/constants"
import AuthService from "./AuthService"
import toastMessages from "stores/toastMessages"
import i18n from "intl/i18n"
import Routes from "@/routes"
import { navigate } from "utils/router"
import { hasMorePages } from "./utils"
import CustomError from "models/CustomError"

export interface IRequestParams {
    serviceName: ApiServiceName
    followLink: IApiFollowLink
    headers?: { [x: string]: string | number | boolean }
    data?: any
    includeEtag?: boolean
    includeFilename?: boolean
    responseType?: ResponseType
    signal?: AbortSignal
}

interface IFindLinkParams {
    serviceName: ApiServiceName
    followLink: IApiFollowLink
}

export interface IFollowLinkParams {
    link: IApiLink
    linkParams?: { [x: string]: any }
    linkQuery?: { [x: string]: string | number | boolean | undefined }
    headers?: { [x: string]: string | number | boolean }
    data?: any
    includeEtag?: boolean
    includeFilename?: boolean
    responseType?: ResponseType
    validateStatus?: (status: number) => boolean
    signal?: AbortSignal
}

interface IGetPagedDataParams<ResultDto, Result> {
    service: ApiServiceName
    entity: ApiLinkEntity
    rel: ApiLinkRel
    mapFunc(dto: ResultDto): Result
    pageNumber?: number
    pageSize?: number
    linkParams?: { [x: string]: any }
    query?: IApiFollowLink["query"]
}

class ApiService {

    #cache = new Map<ApiServiceName, IApiLink[]>()
    #defaultLanguage = ""

    async request(params: IRequestParams): Promise<any> {
        const {
            serviceName,
            followLink,
            headers,
            data,
            includeEtag,
            includeFilename,
            responseType,
            signal
        } = params
        const { link, rootLinks } = await this.findLink({ serviceName, followLink })

        if (!link) {
            console.log("Link not found", followLink, rootLinks)
            throw new Error("Link not found")
        }

        return this.followLink({
            link,
            headers,
            linkParams: followLink.params,
            linkQuery: followLink.query,
            data,
            includeEtag,
            includeFilename,
            responseType,
            signal
        })

    }

    async fetchRootLinks(serviceName: ApiServiceName): Promise<IApiLink[]> {
        const rootLinks = (await axios({
            method: "GET",
            url: `/${serviceName}/`,
        })).data

        this.#cache.set(serviceName, rootLinks)
        return rootLinks
    }

    async findLink({ serviceName, followLink }: IFindLinkParams): Promise<{ link: IApiLink | undefined, rootLinks: IApiLink[] }> {
        const cachedRootLinks = this.#cache.get(serviceName)
        let rootLinks: IApiLink[]

        if (cachedRootLinks) {
            rootLinks = cachedRootLinks
        } else {
            rootLinks = await this.fetchRootLinks(serviceName)
        }

        return {
            rootLinks,
            link: rootLinks.find(rootLink => (
                rootLink.entity === followLink.entity
                && rootLink.rel === followLink.rel
                && (followLink.params && Object.keys(followLink.params).length > 0 ? rootLink.templated === true : true)
            ))
        }
    }

    #prepareQueryParams(params?: { [x: string]: any }, addQueryPrefix?: boolean) {
        if (!params) return ""
        return qs.stringify(params, {
            addQueryPrefix,
            skipNulls: true,
            // omit empty strings, null, and undefined
            filter: (_, value) => value === "" ? undefined : (value ?? undefined),
        })
    }

    async followLink(params: IFollowLinkParams): Promise<any> {
        const {
            link,
            linkParams,
            linkQuery,
            headers,
            data,
            includeEtag,
            includeFilename,
            responseType,
            validateStatus,
            signal
        } = params
        let url = link.href
        if (link.templated && linkParams) {
            const keys = Object.keys(linkParams)
            for (const key of keys) {
                const regExp = new RegExp(`{${key}}`, "g")
                let value
                if (typeof linkParams[key] === "object") {
                    value = this.#prepareQueryParams(linkParams[key])
                } else {
                    value = encodeURIComponent(linkParams[key].toString())
                }
                url = url.replace(regExp, value)
            }
        }
        const queryParams = this.#prepareQueryParams(linkQuery, true)
        const resp = await axios({
            method: link.method,
            url: `${url}${queryParams}`,
            headers,
            data,
            responseType,
            signal,
            // Only set validateStatus when the caller provided one. Passing
            // `validateStatus: undefined` makes axios 1.x resolve EVERY status
            // (incl. 4xx/5xx), which swallows errors — e.g. a 403 ownership
            // conflict would resolve as success instead of rejecting, so the
            // "edited by another user" modal never showed. Omitting the key
            // lets axios apply its default (reject non-2xx).
            ...(validateStatus && { validateStatus }),
        })
        if (includeEtag || includeFilename) {
            const eTagParts = resp.headers.etag?.match(/"([^"]+)"/)
            return {
                ...(responseType !== "blob" ? resp.data || {} : {}),
                ...(responseType === "blob" ? { blob: resp.data } : {}),
                ...(eTagParts && eTagParts[1] ? { eTag: eTagParts[1] } : {}),
                fileName: includeFilename ? decodeURIComponent(resp.headers["content-disposition"]?.match(/filename\*=UTF-8''(.+)$/)?.[1]) : undefined
            }
        }
        return resp.data
    }

    async getPagedData<ResultDto, Result>(params: IGetPagedDataParams<ResultDto, Result>): Promise<IMultipleEntities<Result>> {
        try {
            const {
                service,
                entity,
                rel,
                pageSize,
                pageNumber,
                mapFunc,
                linkParams,
                query,
            } = params

            const requestQuery: IApiFollowLink["query"] = {
                PageSize: pageSize,
                PageNumber: pageNumber ?? undefined,
                ...(linkParams?.query || query),
            }

            const data: IMultipleEntitiesResponse<ResultDto> = await this.request({
                serviceName: service,
                followLink: {
                    entity,
                    rel,
                    params: linkParams ? { ...linkParams, query: requestQuery } : undefined,
                    query: linkParams?.query ? undefined : requestQuery,
                },
            })

            return {
                list: data.entities.map(mapFunc),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getPagedData({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getDefaultHeaders() {
        const user = await AuthService.getOidcUser()
        let token = ""

        if (user) {
            if (user.access_token) {
                token = user.access_token
            } else {
                token = await AuthService.renewToken()
            }
        }

        if (!this.#defaultLanguage) {
            try {
                this.#defaultLanguage = (navigator.language || (navigator as any).userLanguage).split("-")[0].toLowerCase()
            } catch (e) {
                this.#defaultLanguage = config.defaultLanguage
            }
        }

        return {
            Accept: "application/json",
            Authorization: "Bearer " + token,
            Language: this.#defaultLanguage
        }
    }
}

const apiService = new ApiService()

axios.defaults.baseURL = config.apiRoot
// drop axios's built-in Accept default so the request interceptor can tell
// explicit per-request Accept headers (e.g. Excel/PDF downloads) from defaults
delete axios.defaults.headers.common["Accept"]

const refreshAuthLogic = async failedRequest => {
    try {
        if (failedRequest.response.status === 403) {
            if (failedRequest.response.headers.inboundpolicy === "true") {
                return AuthService.requestAccess()
            }
            return Promise.reject(failedRequest)
        }
        const newToken = await AuthService.renewToken()
        failedRequest.response.config.headers.set("Authorization", "Bearer " + newToken)
        return Promise.resolve()
    } catch (e) {
        console.log(e)
        await AuthService.login()
        return Promise.reject(failedRequest)
    }
}

const handle409Error = async failedRequest => {
    if (failedRequest.response.status === 409 && failedRequest.response.headers.inboundpolicy === "true") {
        navigate(Routes.noCompany.getPath(), { replace: true })
    }
    return Promise.reject(failedRequest)
}

const handle500Error = async () => {
    toastMessages.show({ title: i18n.t("message.systemError"), type: "error", duration: 0 })
}

axios.interceptors.request.use(
    async reqConfig => {
        const defaultHeaders = await apiService.getDefaultHeaders()
        for (const [name, value] of Object.entries(defaultHeaders)) {
            reqConfig.headers.set(name, value, false)
        }
        return reqConfig
    },
    err => {
        console.log(err)
        return Promise.reject(err)
    },
)


createAuthRefreshInterceptor(axios, refreshAuthLogic, { statusCodes: [401, 403] })
createAuthRefreshInterceptor(axios, handle409Error, { statusCodes: [409] })
createAuthRefreshInterceptor(axios, handle500Error, { statusCodes: [500] })

export default apiService
